import { ChangeDetectorRef, Component, HostListener, Inject, Input, OnInit, ViewChild } from '@angular/core';

import { ConfigService } from '../../../config/config.service';
import { DatatableComponent } from '@swimlane/ngx-datatable';
import { QueryService } from '../../../shared/service/query.service';
import 'moment-timezone';
import { TranslateService } from '@ngx-translate/core';
import { DetailFormDataModel } from '../../../config/model/detail-form-data.model';
import { Observable } from 'rxjs';
import { LayerContainerService } from '../../../shared/service/layer-container.service';
import { CONSTANTS } from '../../../config/constants.module';
import { TableSettings } from '../../../config/model/listview-config.model';
import { DetailViewFeatureParams } from '../detail-view.model';
import { ColumneConfig } from '../../../config/model/detailview-config.model';
import { ErrorHandlingService } from '../../../core/service/error-handling.service';

@Component({
  selector: 'part-features-table',
  templateUrl: './part-features-table.component.html',
  styleUrls: ['part-features-table.component.scss'],
  standalone: false,
})
export class PartFeaturesTableComponent implements OnInit {
  // Referenz auf das DOM Element featuresTable
  @ViewChild('partFeaturesTable', { static: false }) partFeaturesTable: DatatableComponent;
  @Input() detailFormDataModel: DetailFormDataModel;
  // @Input() currentArbeitsbereichLyrs: any[];
  @Input() featureparams: DetailViewFeatureParams;
  // Stand  von Current Table Rows
  public currentPartGraphics: __esri.Graphic[];
  // Definition der Spalten (kommt aus config)
  public columnDefinition: Array<any>;
  // Definition der Tabelle (kommt aus config)
  public tableSettings: TableSettings;
  // Flag für Angular um das DOM zu rerendern wenn Table Size mit CSS aktualisiert wird
  public rerender = false;
  public tableMessageOptions: any;
  public summeFlaechenAusgleichen: number;
  public summeFlaecheBerechnet: number;

  constructor(
    private configService: ConfigService,
    private changeDetectorRef: ChangeDetectorRef,
    private errorHandlingService: ErrorHandlingService,
    private queryService: QueryService,
    private translateService: TranslateService,
    private layerContainerService: LayerContainerService,
    @Inject(CONSTANTS) private CONST: any
  ) {
    // Initialize default page/size and table settings
    this.tableSettings = this.configService.listViewConfig.table;

    // initial table message options
    this.tableMessageOptions = {
      selectedMessage: false,
    };
    this.translateService.get(this.configService.listViewConfig.table.messages.emptyMessage).subscribe(label => {
      this.tableMessageOptions.emptyMessage = `<h1 class="table-empty-message">${label}</h1>`;
    });
    this.translateService.get(this.configService.listViewConfig.table.messages.totalMessage).subscribe(label => {
      this.tableMessageOptions.totalMessage = label;
    });
  }

  // welche Spalten sollen die Summen bekommen?
  readonly SUM_COL_PROP_FLAECHE = 'attributes.FLAECHE_AUSGEGLICHEN';
  readonly SUM_COL_PROP_GIS = 'attributes.FLAECHE_BERECHNET';

  get footerGridColumns(): string {
    return (this.columnDefinition ?? []).map(c => (c?.width ? `${c.width}px` : '1fr')).join(' ');
  }

  get sumIndexFlaeche(): number {
    return (this.columnDefinition ?? []).findIndex(c => c?.prop === this.SUM_COL_PROP_FLAECHE);
  }

  get sumIndexGis(): number {
    return (this.columnDefinition ?? []).findIndex(c => c?.prop === this.SUM_COL_PROP_GIS);
  }

  ngOnInit() {
    this.initialTableSettings();
  }

  /**
   * @function getSelectedPartFeatures
   * @returns {Observable<any>}
   * @param joinAttrValue
   * @param joinAttr
   * @param partFeaturesLyrUrl
   */
  private getSelectedPartFeatures(
    joinAttrValue: string,
    joinAttr: string,
    partFeaturesLyrUrl: string
  ): Observable<any> {
    const whereQuery = `${joinAttr} = '${joinAttrValue}'`;
    const query = this.queryService.buildQuery(true, whereQuery);
    const errorHandling = this.CONST.LAYER_ERROR.GET_FEATURES_FROM_LAYER_WITH_GEOMETRY;
    return this.queryService.execute(partFeaturesLyrUrl, query, errorHandling);
  }

  /* Public methods, for use in templates
  =========================== */
  /**
   * @function onResize
   * @description On Browser Window resize, refresh ngx-datatable in
   * DOM and rerender the table according to responsive css
   */
  @HostListener('window:resize', ['$event'])
  onBrowserResize() {
    setTimeout(() => {
      this.rerender = true;
      this.changeDetectorRef.detectChanges();
      this.rerender = false;
    }, 1000);
  }

  /* Ngx-Datatable : Event-Listeners
  ========================= */
  /**
   * @function initialTableSettings
   * @description Initialisiert die initial Graphicliste als Initialliste und Currentliste.
   * Die im Template angezeigte Liste ist die currentPartGraphics Liste. Liest die Konfiguration
   * für die Anzeige des Tables sowie die Spaltendefinition aus dem ConfigService und
   * setzt die properties für das Template fest.
   */
  private initialTableSettings(): void {
    const config = this.detailFormDataModel.partFeaturesTableConfig;
    const joinAttributeValue: string = this.detailFormDataModel.selectedFeature.attributes[config.queryAttribute];
    const lyrId = config.layerId;
    const arbeitsBereichLyr: any = this.layerContainerService.layerList.filter(
      l => l.fkLayer === this.featureparams.fkIdentifyLayer
    )[0];
    const partFeaturesLyr = arbeitsBereichLyr.esriLayer.sublayers.items.filter((f: any) => f.id === lyrId)[0];
    this.getSelectedPartFeatures(joinAttributeValue, config.joinAttribute, partFeaturesLyr.url).subscribe(
      featureSet => {
        if (featureSet.features.length > 0) {
          this.summeFlaechenAusgleichen = this.calculateSum(featureSet.features, 'FLAECHE_AUSGEGLICHEN');
          this.summeFlaecheBerechnet = this.calculateSum(featureSet.features, 'FLAECHE_BERECHNET');
          this.currentPartGraphics = featureSet.features;
          this.columnDefinition = this.getColumnDefinition();
        }
      },
      error => {
        this.errorHandlingService.handleLayerError(error);
      }
    );
  }

  private calculateSum(sumFeatures: any, sumAttribute: string): number {
    let sum = 0;
    sumFeatures.forEach((feature: any) => {
      sum = sum + feature.attributes[sumAttribute];
    });
    return sum;
  }

  private getColumnDefinition(): Array<ColumneConfig> {
    this.detailFormDataModel.partFeaturesTableConfig.columns.forEach(column => {
      column.name = this.translateService.instant(column.name);
    });
    return this.detailFormDataModel.partFeaturesTableConfig.columns;
  }
}
